import React, { useRef, useState } from 'react';

import { Popover } from 'antd';

import styled from 'styled-components';

import { useLayoutContext } from '@contexts/LayoutContext';
import { useUserGuiConfig } from '@contexts/UserGuiConfigContext';

import NwDrawer from '@components/Gui/NwDrawer';
import TagPickerPanel, { DRAWER_PANEL_MAX_HEIGHT } from '@components/TagPicker/TagPickerPanel';

const POPOVER_WIDTH = 320;
// Cornice del popover attorno al pannello, misurata in pagina: titolo 34px piu' i 12px
// di padding sopra e sotto di .ant-popover-inner-content.
const POPOVER_CHROME = 58;
// Respiro fra il popover e il bordo della finestra.
const VIEWPORT_MARGIN = 24;
// Sotto questa soglia il pannello diventa inutilizzabile: meglio sforare di poco.
const MIN_PANEL_HEIGHT = 220;

// Il trigger e' un elemento qualunque passato dal consumer: qui diventa un controllo vero,
// raggiungibile da tastiera e con un anello di focus visibile.
const TriggerWrapper = styled.span`
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.PrimaryColor};
        outline-offset: 2px;
        border-radius: 4px;
    }
`;

/**
 * Contenitore del TagPickerPanel. Un solo componente al posto delle coppie duplicate
 * TagManager/TagManagerDrawer e FilterTagSelector/FilterTagSelectorPopover.
 *
 * container  'popover' (default), 'drawer', oppure 'inline' quando il pannello vive gia'
 *            dentro un contenitore proprio (es. i footer delle wall).
 *            Su mobile il popover non ha spazio e diventa comunque un drawer.
 *            Se userGuiConfig.other.openTagPickerInDrawer e' true, il popover diventa drawer
 *            anche su desktop. BulkTagPicker non usa questo contenitore.
 * trigger    elemento cliccabile che apre il pannello; ignorato con container 'inline'.
 */
const TagPicker = ({
    container = 'popover',
    title = 'Tags',
    trigger,
    triggerLabel,
    zIndex,
    readOnly,
    ...panelProps
}) => {
    const [open, setOpen] = useState(false);
    const [panelMaxHeight, setPanelMaxHeight] = useState();
    const triggerRef = useRef();
    const { isMobile } = useLayoutContext();
    const userGuiConfig = useUserGuiConfig();
    // BulkTagPicker non passa da qui: resta il drawer del footer, col counter sulla selezione
    const preferDrawer = Boolean(userGuiConfig && userGuiConfig.other && userGuiConfig.other.openTagPickerInDrawer);

    if (container === 'inline') {
        return <TagPickerPanel {...panelProps} />;
    }

    // in sola lettura resta solo il trigger: nessun pannello da aprire
    if (readOnly) {
        return trigger;
    }

    const closePanel = () => setOpen(false);

    // Il pannello prende quasi tutto lo spazio libero dal lato dove il popover si aprira'.
    // Resta un max-height, non un height: con pochi tag il pannello si accorcia da solo.
    const measurePanelMaxHeight = () => {
        if (!triggerRef.current) {
            return;
        }
        const rect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const available = Math.max(spaceBelow, spaceAbove) - VIEWPORT_MARGIN - POPOVER_CHROME;
        setPanelMaxHeight(`${Math.max(available, MIN_PANEL_HEIGHT)}px`);
    };

    const handleOpenChange = (visible) => {
        if (visible) {
            measurePanelMaxHeight();
        }
        setOpen(visible);
    };

    // Invio e spazio devono fare quello che fa il click: il trigger e' un div, non un button
    const handleTriggerKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOpenChange(!open);
        }
    };

    const triggerA11yProps = {
        role: 'button',
        tabIndex: 0,
        'aria-haspopup': 'dialog',
        'aria-expanded': open,
        // senza etichetta il nome verrebbe dal contenuto, che a volte e' la sola icona
        'aria-label': triggerLabel,
        onKeyDown: handleTriggerKeyDown,
    };

    if (isMobile || container === 'drawer' || preferDrawer) {
        return (
            <>
                <TriggerWrapper {...triggerA11yProps} onClick={() => setOpen(true)}>
                    {trigger}
                </TriggerWrapper>
                {open && (
                    <NwDrawer title={title} zIndex={zIndex} onClose={closePanel}>
                        <TagPickerPanel
                            maxHeight={DRAWER_PANEL_MAX_HEIGHT}
                            {...panelProps}
                            onRequestClose={closePanel}
                        />
                    </NwDrawer>
                )}
            </>
        );
    }

    // l'apertura la gestisce il Popover con trigger="click": un onClick anche sul trigger
    // impedirebbe la chiusura al secondo click
    return (
        <Popover
            // ancorato a sinistra: il trigger cresce verso destra quando si aggiungono tag,
            // quindi il bordo sinistro e' l'unico punto che non si sposta sotto al pannello aperto
            placement="bottomLeft"
            title={title}
            trigger="click"
            open={open}
            // la larghezza va sull'inner: su .ant-popover resta il box esterno a misura
            // fissa mentre l'inner si dimensiona sul contenuto, lasciando un vuoto a destra.
            // La classe serve a far riempire quella larghezza anche a inner-content,
            // che antd 4.24 tiene a width: max-content (vedi _tooltips_popovers.less)
            overlayClassName="tag-picker-popover"
            overlayInnerStyle={{ width: POPOVER_WIDTH }}
            // senza questo antd tiene il pannello montato e solo nascosto: alla riapertura
            // non ci sarebbe un mount a rimettere a fuoco la ricerca, e resterebbe dentro
            // il testo cercato l'ultima volta
            destroyTooltipOnHide
            onOpenChange={handleOpenChange}
            content={<TagPickerPanel maxHeight={panelMaxHeight} {...panelProps} onRequestClose={closePanel} />}
        >
            <TriggerWrapper ref={triggerRef} {...triggerA11yProps}>
                {trigger}
            </TriggerWrapper>
        </Popover>
    );
};

export default TagPicker;
